
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Pixel } from "@/types/database";
import { useAuth } from "@/components/AuthProvider";
import { toast } from "sonner";

export const usePixels = (userId: string | undefined) => {
  const { session } = useAuth();

  return useQuery<Pixel[]>({
    queryKey: ['pixels', userId],
    queryFn: async () => {
      if (!userId || !session) return [];
      
      try {
        // First get the user's own pixels
        const { data: userPixels, error: userPixelsError } = await supabase
          .from('pixels')
          .select('*')
          .eq('user_id', userId);
          
        if (userPixelsError) {
          throw userPixelsError;
        }
        
        // Check if the current user is an organization creator
        const { data: userOrgs, error: userOrgsError } = await supabase
          .from('organizations')
          .select('id')
          .eq('created_by', userId);
        
        if (userOrgsError) {
          console.error("Error fetching user organizations:", userOrgsError);
          return userPixels || [];
        }
        
        // Also check if the current user is a team member
        const { data: teamMemberships, error: teamMembershipError } = await supabase
          .from('team_members')
          .select('organization_id')
          .eq('user_id', userId)
          .eq('status', 'active')
          .not('organization_id', 'is', null);
        
        if (teamMembershipError) {
          console.error("Error fetching team memberships:", teamMembershipError);
          return userPixels || [];
        }
        
        // Combine all organization IDs that the user belongs to
        const userOrgIds = [
          ...(userOrgs || []).map(org => org.id),
          ...(teamMemberships || []).map(membership => membership.organization_id)
        ];
        
        if (userOrgIds.length === 0) {
          // User doesn't belong to any organization
          return userPixels || [];
        }

        // Directly get all pixels that belong to the user's organizations
        const { data: orgPixels, error: orgPixelsError } = await supabase
          .from('pixels')
          .select('*')
          .in('organization_id', userOrgIds);
        
        if (orgPixelsError) {
          console.error("Error fetching organization pixels:", orgPixelsError);
          return userPixels || [];
        }
        
        // Combine user's own pixels with organization pixels
        const allPixels = [
          ...(userPixels || []),
          ...(orgPixels || [])
        ];
        
        // Deduplicate pixels by ID
        const uniquePixels = Array.from(
          new Map(allPixels.map(pixel => [pixel.id, { 
            ...pixel,
            organization_id: pixel.organization_id || null
          }])).values()
        );
        
        return uniquePixels;
      } catch (error: any) {
        toast.error("Failed to load pixels. Please try refreshing the page.");
        throw error; // Rethrow to let React Query handle the error state
      }
    },
    enabled: !!userId && !!session,
    retry: 1,
  });
};
